import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { normalizeLocalDate, todayLocalISO } from "@/lib/date-utils";

export type FluxoCaixa = {
  id: string;
  user_id: string;
  tipo: "Entrada" | "Saída";
  descricao: string;
  categoria: string;
  valor: number;
  data_movimentacao: string;
  forma_pagamento?: string;
  taxa_operadora_percentual?: number;
  valor_taxa_operadora?: number;
  valor_liquido?: number;
  observacoes?: string;
  subtipo?: "movimentacao" | "sangria";
  motivo?: string;
  origem?: "manual" | "recibo" | "estorno_recibo" | string;
  origem_id?: string;
  paciente_id?: string;
  paciente_nome?: string;
  servico?: string;
  responsavel_nome?: string;
  idempotency_key?: string;
  status?: "Ativo" | "Cancelado" | string;
  cancelado_em?: string;
  cancelado_por?: string;
  motivo_cancelamento?: string;
  lancamento_manual_original?: string;
  created_at: string;
  updated_at: string;
};

const normalizeDataMovimentacao = (value: unknown) => {
  return normalizeLocalDate(value as string | Date | null, todayLocalISO());
};

const normalizeFluxoCaixa = <T extends Partial<FluxoCaixa>>(data: T) => ({
  ...data,
  data_movimentacao: normalizeDataMovimentacao(data.data_movimentacao),
});

export function useFluxoCaixa() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const { data: movimentacoes = [], isLoading } = useQuery({
    queryKey: ["fluxo_caixa", activeClinicaId],
    queryFn: async () => {
      const data = await apiList<FluxoCaixa>("fluxo_caixa");
      return data
        .map((item) => normalizeFluxoCaixa(item) as FluxoCaixa)
        .sort((a, b) => String(b.data_movimentacao).localeCompare(String(a.data_movimentacao)));
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<FluxoCaixa, "id" | "user_id" | "created_at" | "updated_at">) => {
      return apiCreate<FluxoCaixa>("fluxo_caixa", {
        ...normalizeFluxoCaixa(data),
        user_id: user?.id,
        responsavel_nome: data.responsavel_nome || user?.nome || user?.email || "Usuário da clínica",
        origem: data.origem || "manual",
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fluxo_caixa", activeClinicaId], refetchType: "all" });
      toast({
        title: "Sucesso",
        description: "Movimentação cadastrada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar movimentação: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<FluxoCaixa> & { id: string }) => {
      return apiUpdate<FluxoCaixa>("fluxo_caixa", id, normalizeFluxoCaixa(data));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fluxo_caixa", activeClinicaId], refetchType: "all" });
      toast({
        title: "Sucesso",
        description: "Movimentação atualizada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao atualizar movimentação: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("fluxo_caixa", id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fluxo_caixa", activeClinicaId], refetchType: "all" });
      toast({
        title: "Sucesso",
        description: "Movimentação excluída com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao excluir movimentação: " + error.message,
        variant: "destructive",
      });
    },
  });

  return {
    movimentacoes,
    isLoading,
    createMovimentacao: createMutation.mutateAsync,
    updateMovimentacao: updateMutation.mutateAsync,
    deleteMovimentacao: deleteMutation.mutateAsync,
  };
}
